import type { ComponentType } from 'react';
import {
  ApiOutlined,
  DingtalkOutlined,
  DiscordOutlined,
  MailOutlined,
  MessageOutlined,
  SendOutlined,
  WechatWorkOutlined,
} from '@ant-design/icons';

import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import type { MaGoi, MaKenh, MucDo, TrangThaiBanGhi, TrangThaiGui } from './types';

type TongTrangThai = Extract<BadgeVariant, 'success' | 'warning' | 'danger' | 'primary' | 'neutral'>;

/** Biểu tượng của từng loại kênh (icon trong màn dùng `@ant-design/icons`). */
export const ICON_KENH: Record<MaKenh, ComponentType<{ className?: string }>> = {
  TELEGRAM: SendOutlined,
  EMAIL: MailOutlined,
  WEBHOOK: ApiOutlined,
  DISCORD: DiscordOutlined,
  DINGTALK: DingtalkOutlined,
  WECOM: WechatWorkOutlined,
  FEISHU: MessageOutlined,
};

/**
 * Trạng thái một lần cảnh báo — nhóm biến thể **trạng thái** của `Badge`. `SUPPRESSED` là hệ thống chặn theo giới
 * hạn người dùng tự đặt, không phải sự cố (`neutral`); `PARTIAL` là việc còn dở (`warning`).
 */
export const BADGE_BAN_GHI: Record<TrangThaiBanGhi, { variant: TongTrangThai; khoa: KhoaDich }> = {
  PENDING: { variant: 'primary', khoa: 'canhBaoMa.banGhi.PENDING' },
  SENT: { variant: 'success', khoa: 'canhBaoMa.banGhi.SENT' },
  PARTIAL: { variant: 'warning', khoa: 'canhBaoMa.banGhi.PARTIAL' },
  FAILED: { variant: 'danger', khoa: 'canhBaoMa.banGhi.FAILED' },
  SUPPRESSED: { variant: 'neutral', khoa: 'canhBaoMa.banGhi.SUPPRESSED' },
};

export const BADGE_GUI: Record<TrangThaiGui, { variant: TongTrangThai; khoa: KhoaDich }> = {
  PENDING: { variant: 'primary', khoa: 'canhBaoMa.gui.PENDING' },
  PROCESSING: { variant: 'primary', khoa: 'canhBaoMa.gui.PROCESSING' },
  SUCCEEDED: { variant: 'success', khoa: 'canhBaoMa.gui.SUCCEEDED' },
  RETRY_SCHEDULED: {
    variant: 'warning',
    khoa: 'canhBaoMa.gui.RETRY_SCHEDULED',
  },
  FAILED_PERMANENT: {
    variant: 'danger',
    khoa: 'canhBaoMa.gui.FAILED_PERMANENT',
  },
  DEAD_LETTERED: { variant: 'danger', khoa: 'canhBaoMa.gui.DEAD_LETTERED' },
  CANCELLED: { variant: 'neutral', khoa: 'canhBaoMa.gui.CANCELLED' },
};

/** Mức độ: nghiêm trọng là `danger`, cảnh báo là `warning`, thông tin là `neutral`. */
export const BADGE_MUC: Record<MucDo, { variant: TongTrangThai; khoa: KhoaDich }> = {
  INFO: { variant: 'neutral', khoa: 'canhBaoMa.muc.INFO' },
  WARNING: { variant: 'warning', khoa: 'canhBaoMa.muc.WARNING' },
  CRITICAL: { variant: 'danger', khoa: 'canhBaoMa.muc.CRITICAL' },
};

/** Loại kênh trong bảng danh mục, ô kênh của tác vụ — nhóm **phân loại**, một tông chung. */
export const TONG_KENH: Extract<BadgeVariant, 'violet' | 'teal' | 'orange' | 'info'> = 'info';

export const GOI: readonly MaGoi[] = ['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];

/** Mặc định của "Cài đặt nâng cao" (ADR-0102 mục 6). */
export const MAC_DINH_TAC_VU = {
  daily_limit: 3,
  min_interval_sec: 600,
  send_from: '00:00',
  send_to: '23:59',
} as const;

/** Số kênh hiện trong một ô trước khi gộp thành `+n`. */
export const SO_KENH_HIEN = 2;
